import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import timeTrackingService from '../services/timeTrackingService';
import companyService from '../services/companyService';

const INACTIVITY_MS = 30000;
const SUCCESS_MS    = 8000;

// ── Spanish pools (30 each) ───────────────────────────────────────────────────
const ES_CLOCK_IN = [
  "¡Hagamos que hoy valga la pena! 💪",
  "Las grandes cosas empiezan con presentarse.",
  "¡Tú puedes. Vamos! 🚀",
  "¿Listo para darlo todo hoy? 🔥",
  "Otro día, otra victoria. 🏆",
  "Cosas buenas por delante. ¡A trabajar!",
  "El equipo es mejor contigo aquí. ⭐",
  "¡Hagamos una pizza increíble hoy! 🍕",
  "Tu mejor trabajo empieza ahora. 🎯",
  "Nuevo comienzo, nueva pizza. ¡Vamos!",
  "Cada turno es una oportunidad de brillar. ✨",
  "¡Hoy es un gran día para hacer grandes pizzas! 🍕",
  "Tu energía contagia a todo el equipo. ⚡",
  "¡El éxito se hornea con dedicación! 🔥",
  "Cada pequeño esfuerzo suma. ¡Adelante!",
  "¡Eres parte de algo especial hoy! 🌟",
  "La excelencia se construye un turno a la vez.",
  "¡Bienvenido de vuelta, campeón! 🏅",
  "Hoy es tu día para brillar. ☀️",
  "¡Que empiece la magia en la cocina! ✨",
  "Tu trabajo hace la diferencia cada día. 💯",
  "¡Vamos con todo, que hoy hay mucho por hacer! 💥",
  "La calidad empieza con tu actitud. 👊",
  "¡El mejor turno del día está por comenzar! 🎉",
  "Cada pizza es una obra de arte — ¡y tú eres el artista! 🎨",
  "¡Juntos hacemos el mejor equipo! 🤝",
  "¡Tu esfuerzo de hoy es el orgullo de mañana! 🌅",
  "¡Manos a la masa! 💪",
  "Hoy también vas a hacer un trabajo increíble. ⭐",
  "¡El secreto está en la pasión que le pones! ❤️",
];

const ES_CLOCK_OUT = [
  "¿Por qué el pizzero renunció? ¡Porque el trabajo era muy picante! 🌶️",
  "Sobreviviste otro turno. Eso merece un trofeo... o al menos una rebanada. 🏅",
  "¿Por qué los panaderos siempre están tranquilos? ¡Porque amasan la situación! 😄",
  "¡Buen turno! Ahora descansa — el horno te va a extrañar. 🔥",
  "¡Hiciste un trabajo increíble hoy! Ve a casa y descansa los pies. 🛋️",
  "¿Cómo se llama una pizza dormida? ¡PiZZZZa! 😴",
  "Otro turno exitoso en el historial. ¡Eres una leyenda! 📖",
  "¿Por qué la pizza nunca gana al póker? Siempre muestra la corteza. 🃏",
  "¡Hora de casa! Te lo ganaste. ¡Hasta la próxima! 👋",
  "¿Cuál es la película favorita de la pizza? ¡Masa Imparable! 🎬",
  "¿Qué le dijo el horno a la pizza? ¡Estás que ardes! 🔥",
  "Terminaste el turno como todo un profesional. ¡Descansa, lo mereces! 😎",
  "¿Por qué la pizza fue al médico? Porque tenía mucho queso. 🧀",
  "¡Misión cumplida! El equipo cuenta contigo. ¡Hasta mañana! 🌙",
  "¿Qué dijo la pizza al chef? ¡Me tienes loco de queso! 😜",
  "Hoy diste lo mejor de ti. ¡Ve a recargar energías! ⚡",
  "¿Cuántas pizzas hacen falta para alegrar el día? Solo una — ¡y tú la hiciste! 🍕",
  "¡Gran trabajo hoy! La pizza de mañana ya te extraña. 😅",
  "¿Por qué la pizza nunca llega tarde? Porque sabe que el tiempo es queso. ⏰",
  "Eres el ingrediente secreto de este equipo. ¡Buen descanso! 🌟",
  "¿Qué le dijo una pizza a otra? ¡Eres mi media mitad! 🍕❤️",
  "Turno completado con éxito. ¡Ahora a comer algo rico! 😋",
  "¿Por qué la pizza es tan filosófica? Porque siempre busca su complemento. 🤔",
  "¡Excelente turno! Que la noche te trate tan bien como tú al equipo. 🌙",
  "¿Qué hace una pizza en la playa? ¡Tomar el sol-ero! ☀️",
  "Otro día de gloria en la cocina. ¡A casa a descansar, héroe! 🦸",
  "¿Por qué las pizzas son tan generosas? Porque se parten por los demás. 😄",
  "¡Increíble trabajo hoy! Mereces todo el descanso del mundo. 💤",
  "¿Qué dijo el queso a la pizza? ¡Sin ti no soy nada! 🧀",
  "¡Hasta el próximo turno, crack! El equipo te espera con los brazos abiertos. 🤗",
];

function randomPick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ── Live English content fetchers ─────────────────────────────────────────────
async function fetchLiveQuote() {
  try {
    const res = await fetch('https://api.adviceslip.com/advice', { cache: 'no-cache' });
    const data = await res.json();
    return data?.slip?.advice || null;
  } catch { return null; }
}

async function fetchLiveJoke() {
  try {
    const res = await fetch('https://v2.jokeapi.dev/joke/Pun,Misc?type=single&safe-mode');
    const data = await res.json();
    return data?.joke || null;
  } catch { return null; }
}

// ── PIN pad layout ────────────────────────────────────────────────────────────
const PAD = [
  ['1','2','3'],
  ['4','5','6'],
  ['7','8','9'],
  ['C','0','✓'],
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ textAlign: 'center', color: '#aab4c0', marginBottom: 8 }}>
      <div style={{ fontSize: '3rem', fontWeight: 700, letterSpacing: 4 }}>
        {now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </div>
      <div style={{ fontSize: '1.05rem' }}>
        {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
      </div>
    </div>
  );
}

function PinDots({ length }) {
  return (
    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', margin: '16px 0' }}>
      {[0,1,2,3,4,5].map(i => (
        <div key={i} style={{
          width: 18, height: 18, borderRadius: '50%',
          background: i < length ? '#5b9bd5' : 'rgba(255,255,255,0.15)',
          border: '2px solid rgba(255,255,255,0.25)',
          transition: 'background 0.12s',
        }} />
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function TimeClock() {
  const [searchParams] = useSearchParams();
  const urlCompanyId = searchParams.get('companyId');
  const [companyId, setCompanyId] = useState(urlCompanyId);
  const [resolving, setResolving] = useState(!urlCompanyId);

  // If no ?companyId in the URL, resolve from the current subdomain (tenant domain).
  // This means on pizzacini.quicklogapp.com/time-clock the param is never needed.
  useEffect(() => {
    if (urlCompanyId) return;
    companyService.getByDomain(window.location.hostname)
      .then(data => { if (data?.companyId) setCompanyId(String(data.companyId)); })
      .catch(() => {})
      .finally(() => setResolving(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register SW and send companyId so the dynamic manifest has the right start_url for any tenant
  useEffect(() => {
    if (!companyId || !('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js').then(reg => {
      const send = () => (reg.active || reg.installing || reg.waiting)
        ?.postMessage({ type: 'SET_CLOCK_COMPANY', companyId });
      if (reg.active) { send(); return; }
      const sw = reg.installing || reg.waiting;
      if (sw) sw.addEventListener('statechange', e => { if (e.target.state === 'activated') send(); });
    }).catch(() => {});
  }, [companyId]);

  const [pin, setPin]                       = useState('');
  const [phase, setPhase]                   = useState('enter_pin'); // enter_pin | confirming | success | error
  const [employee, setEmployee]             = useState(null);   // { firstName, lastName }
  const [action, setAction]                 = useState(null);   // 'clock_in' | 'clock_out'
  const [openEntry, setOpenEntry]           = useState(null);
  const [successData, setSuccessData]       = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMsg, setErrorMsg]             = useState('');
  const [shake, setShake]                   = useState(false);
  const [checking, setChecking]             = useState(false);
  const [punching, setPunching]             = useState(false);

  const inactivityRef = useRef(null);

  const resetToPin = useCallback(() => {
    setPin('');
    setPhase('enter_pin');
    setEmployee(null);
    setAction(null);
    setOpenEntry(null);
    setErrorMsg('');
    setChecking(false);
    setPunching(false);
  }, []);

  const bumpInactivity = useCallback(() => {
    if (inactivityRef.current) clearTimeout(inactivityRef.current);
    inactivityRef.current = setTimeout(resetToPin, INACTIVITY_MS);
  }, [resetToPin]);

  // Reset inactivity on any interaction
  useEffect(() => {
    const handler = () => bumpInactivity();
    window.addEventListener('touchstart', handler);
    window.addEventListener('mousedown', handler);
    window.addEventListener('keydown', handler);
    bumpInactivity();
    return () => {
      window.removeEventListener('touchstart', handler);
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('keydown', handler);
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
    };
  }, [bumpInactivity]);

  const handleKey = useCallback((key) => {
    bumpInactivity();
    if (phase !== 'enter_pin') return;

    if (key === 'C') {
      setPin('');
      setErrorMsg('');
      return;
    }

    if (key === '✓') {
      if (pin.length < 4) {
        triggerShake('PIN must be 4–6 digits');
        return;
      }
      lookupPin(pin);
      return;
    }

    if (pin.length < 6 && /\d/.test(key)) {
      const next = pin + key;
      setPin(next);
      setErrorMsg('');
      if (next.length === 6) lookupPin(next);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, pin, bumpInactivity]);

  // Hardware keyboard support
  useEffect(() => {
    const handler = (e) => {
      if (e.key >= '0' && e.key <= '9') handleKey(e.key);
      else if (e.key === 'Backspace') handleKey('C');
      else if (e.key === 'Enter') handleKey('✓');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleKey]);

  function triggerShake(msg) {
    setErrorMsg(msg);
    setShake(true);
    setTimeout(() => { setShake(false); setPin(''); setErrorMsg(''); }, 2000);
  }

  async function lookupPin(currentPin) {
    if (!companyId) { triggerShake('No company configured'); return; }
    setChecking(true);
    try {
      const data = await timeTrackingService.getTerminalStatus(companyId, currentPin);
      setEmployee(data.employee);
      setAction(data.action);
      setOpenEntry(data.openEntry);
      setPhase('confirming');
    } catch (err) {
      const msg = err?.response?.data?.error || 'PIN not recognized';
      triggerShake(msg);
    } finally {
      setChecking(false);
    }
  }

  async function confirmPunch() {
    if (punching) return;
    setPunching(true);
    try {
      const isClockIn = action === 'clock_in';
      const [data, liveEn] = await Promise.all([
        timeTrackingService.punch(companyId, pin),
        isClockIn ? fetchLiveQuote() : fetchLiveJoke(),
      ]);
      setSuccessData(data);
      setSuccessMessage({ en: liveEn, es: randomPick(isClockIn ? ES_CLOCK_IN : ES_CLOCK_OUT) });
      setPhase('success');
      if (data.isBirthday) {
        setTimeout(() => {
          confetti({ particleCount: 150, spread: 80, origin: { y: 0.5 }, colors: ['#ff6b6b','#ffd93d','#6bcb77','#4d96ff','#ff6ef7'] });
          confetti({ particleCount: 80, angle: 60,  spread: 60, origin: { x: 0, y: 0.6 } });
          confetti({ particleCount: 80, angle: 120, spread: 60, origin: { x: 1, y: 0.6 } });
        }, 200);
      }
      setTimeout(resetToPin, data.isBirthday ? 7000 : SUCCESS_MS);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Punch failed';
      triggerShake(msg);
      setPhase('enter_pin');
    } finally {
      setPunching(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const BG = '#0f1923';
  const CARD_BG = '#1a2535';

  if (!companyId) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontFamily: 'sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48 }}>⏱</div>
          {resolving
            ? <div style={{ marginTop: 12 }}>Loading…</div>
            : <div style={{ marginTop: 12 }}>No company configured.<br />Navigate to your company URL (e.g. <code>yourcompany.quicklogapp.com/time-clock</code>).</div>}
        </div>
      </div>
    );
  }

  // ── Success screen ─────────────────────────────────────────────────────────
  if (phase === 'success' && successData) {
    const isIn = successData.action === 'clock_in';
    const { firstName, lastName } = successData.employee;
    const isBirthday = successData.isBirthday;
    return (
      <div style={{ minHeight: '100vh', background: isBirthday ? '#1a0a2e' : (isIn ? '#0d3320' : '#331a0d'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', color: '#fff', padding: 40 }}>
          <div style={{ fontSize: 80, marginBottom: 16 }}>{isBirthday ? '🎂' : (isIn ? '✅' : '👋')}</div>
          <div style={{ fontSize: '2.5rem', fontWeight: 700 }}>{firstName} {lastName}</div>
          {isBirthday ? (
            <div style={{ fontSize: '1.8rem', marginTop: 12, color: '#ffd93d', fontWeight: 600 }}>
              Happy Birthday! 🎉
            </div>
          ) : (
            <div style={{ fontSize: '1.5rem', marginTop: 12, color: isIn ? '#5eda9f' : '#ffa057' }}>
              {isIn ? 'Clocked In!' : 'Clocked Out!'}
            </div>
          )}
          {!isIn && successData.entry?.netHours != null && (
            <div style={{ marginTop: 10, color: '#aaa', fontSize: '1rem' }}>
              {successData.entry.netHours.toFixed(2)} hrs worked
            </div>
          )}
          {successMessage && !isBirthday && (
            <div style={{ marginTop: 20, padding: '16px 24px', borderRadius: 10, background: 'rgba(255,255,255,0.07)', maxWidth: 480, margin: '20px auto 0' }}>
              {successMessage.en && (
                <div style={{ color: '#fff', fontSize: '1.2rem', lineHeight: 1.5 }}>{successMessage.en}</div>
              )}
              <div style={{ color: '#aab4c0', fontSize: '1.05rem', marginTop: successMessage.en ? 8 : 0, fontStyle: 'italic' }}>{successMessage.es}</div>
            </div>
          )}
          <div style={{ marginTop: 16, color: '#555', fontSize: '0.85rem' }}>Returning in a moment…</div>
        </div>
      </div>
    );
  }

  // ── Confirm screen ─────────────────────────────────────────────────────────
  if (phase === 'confirming' && employee) {
    const isIn = action === 'clock_in';
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: 24 }}>
        <Clock />
        <div style={{ background: CARD_BG, borderRadius: 16, padding: '32px 40px', textAlign: 'center', maxWidth: 360, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#fff' }}>{employee.firstName} {employee.lastName}</div>
          {openEntry && (
            <div style={{ color: '#aab4c0', fontSize: '0.85rem', marginTop: 6 }}>
              Clocked in at {new Date(openEntry.clockIn).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
          <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button
              onClick={confirmPunch}
              disabled={punching}
              style={{
                padding: '18px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
                fontSize: '1.2rem', fontWeight: 700, letterSpacing: 1,
                background: isIn ? '#1a7a45' : '#c05a10', color: '#fff',
                opacity: punching ? 0.6 : 1,
              }}
            >
              {punching ? '…' : (isIn ? '🟢 CLOCK IN' : '🟠 CLOCK OUT')}
            </button>
            <button
              onClick={resetToPin}
              style={{ padding: '12px 0', borderRadius: 12, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#aaa', fontSize: '0.95rem', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── PIN entry screen ───────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: 24 }}>
      <Clock />

      <div
        style={{
          background: CARD_BG, borderRadius: 16, padding: '28px 32px', maxWidth: 340, width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          animation: shake ? 'ttShake 0.4s ease' : 'none',
        }}
      >
        <div style={{ textAlign: 'center', color: '#fff', fontSize: '1.1rem', fontWeight: 600, marginBottom: 4 }}>
          Enter your PIN
        </div>

        <PinDots length={pin.length} />

        {errorMsg && (
          <div style={{ textAlign: 'center', color: '#ff6b6b', fontSize: '0.85rem', marginBottom: 8 }}>
            {errorMsg}
          </div>
        )}

        {checking && (
          <div style={{ textAlign: 'center', color: '#5b9bd5', fontSize: '0.85rem', marginBottom: 8 }}>
            Checking…
          </div>
        )}

        {/* PIN Pad */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 8 }}>
          {PAD.flat().map((key) => {
            const isClear   = key === 'C';
            const isConfirm = key === '✓';
            return (
              <button
                key={key}
                onClick={() => handleKey(key)}
                style={{
                  padding: '20px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                  fontSize: isConfirm || isClear ? '1.3rem' : '1.5rem',
                  fontWeight: 600,
                  background: isConfirm ? '#1a5f8a' : isClear ? '#5c2020' : 'rgba(255,255,255,0.08)',
                  color: '#fff',
                  transition: 'background 0.1s, transform 0.07s',
                  userSelect: 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onMouseDown={e => e.currentTarget.style.transform = 'scale(0.94)'}
                onMouseUp={e => e.currentTarget.style.transform = ''}
                onTouchStart={e => e.currentTarget.style.transform = 'scale(0.94)'}
                onTouchEnd={e => e.currentTarget.style.transform = ''}
              >
                {key}
              </button>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes ttShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-10px); }
          40% { transform: translateX(10px); }
          60% { transform: translateX(-8px); }
          80% { transform: translateX(8px); }
        }
      `}</style>
    </div>
  );
}
